import type { ISidebarItems } from "@/components/layout/sidebar/types";
import { ISidebarChildTypeEnum } from "@/components/layout/sidebar/types";
import { IRoleEnum } from "@/interfaces";
import { routePath } from "@routes/path";

export const getSidebarOtherItems = (
  role: IRoleEnum | null,
): ISidebarItems[] => [
  {
    iconName: "Settings2Line",
    name: "common.settings",
    items: {
      path:
        role === IRoleEnum.Student
          ? routePath.student.settings.home()
          : routePath.settings.home(),
    },
  },
  {
    iconName: "HeadphoneLine",
    name: "common.support",
    items: { path: routePath.support.home() },
  },
];

export const sidebarRoleItems: Record<IRoleEnum, ISidebarItems[]> = {
  [IRoleEnum.SUPER_ADMIN]: [
    {
      name: "superAdmin.dashboard.name",
      iconName: "LayoutGridLine",
      items: {
        path: routePath.dashboard.home(),
      },
    },
    {
      name: "superAdmin.userManagement.name",
      iconName: "GroupLine",
      items: {
        path: routePath.userManagement.users.list(),
      },
    },
    {
      name: "superAdmin.lessonAndCourseManagement.name",
      iconName: "GraduationCapLine",
      items: {
        path: routePath.courseManagement.list(),
        child: [
          {
            name: "superAdmin.lessonAndCourseManagement.courseManagement.name",
            path: routePath.courseManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "BookLine",
          },
          {
            name: "superAdmin.lessonAndCourseManagement.trainingCourseManagement.name",
            path: routePath.trainingCourseManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "GraduationCapLine",
          },
          {
            name: "superAdmin.lessonAndCourseManagement.trainingCourseAssistantManagement.name",
            path: routePath.trainingCourseAssistantManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "UserAddLine",
          },
          {
            name: "superAdmin.lessonAndCourseManagement.codeManagement.name",
            path: routePath.codeManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "Coupon3Line",
          },
        ],
      },
    },
    {
      name: "superAdmin.contentAndExamManagement.name",
      iconName: "Slideshow4Line",
      items: {
        path: routePath.contentManagement.list(),
        child: [
          {
            name: "superAdmin.contentAndExamManagement.contentManagement.name",
            path: routePath.contentManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "Slideshow4Line",
          },
          {
            name: "superAdmin.contentAndExamManagement.onlineExamManagement.name",
            path: routePath.onlineExamManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "FileEditLine",
          },
          {
            name: "superAdmin.contentAndExamManagement.descriptiveExamManagement.name",
            path: routePath.descriptiveExamManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "FileChartLine",
          },
        ],
      },
    },
    {
      name: "superAdmin.channelManagement.name",
      iconName: "ChatQuoteLine",
      items: {
        path: routePath.channelManagement.list(),
      },
    },
    {
      name: "superAdmin.commentManagement.name",
      iconName: "QuestionAnswerLine",
      items: {
        path: routePath.commentManagement.list(),
        child: [
          {
            name: "superAdmin.commentManagement.name",
            path: routePath.commentManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "QuestionAnswerLine",
          },
          {
            name: "superAdmin.commentManagement.popularCourses.name",
            path: routePath.popularCourses.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "HeartLine",
          },
          {
            name: "superAdmin.commentManagement.teacherRank.name",
            path: routePath.teacherRank.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "MedalLine",
          },
        ],
      },
    },
    {
      name: "superAdmin.contactManagement.name",
      items: {
        path: routePath.contactManagement.list(),
      },
      iconName: "MailDownloadLine",
    },
    {
      name: "superAdmin.blogPost.name",
      items: {
        path: routePath.blogPost.list(),
      },
      iconName: "NewsLine",
    },
    {
      name: "superAdmin.notifAndMessage.name",
      iconName: "Notification3Line",
      items: {
        path: routePath.notificationManagement.list(),
        child: [
          {
            name: "superAdmin.notifAndMessage.notifManagement.name",
            path: routePath.notificationManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "Notification3Line",
          },
          {
            name: "superAdmin.notifAndMessage.messageManagement.name",
            path: routePath.messagesManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "Message3Line",
          },
        ],
      },
    },
  ],
  [IRoleEnum.Teacher]: [
    {
      name: "superAdmin.dashboard.name",
      iconName: "LayoutGridLine",
      items: {
        path: routePath.dashboard.home(),
      },
    },
    {
      name: "teacher.myTrainingCourses.name",
      iconName: "Slideshow4Line",
      items: {
        path: routePath.contentManagement.list(),
        child: [
          {
            name: "superAdmin.contentAndExamManagement.contentManagement.name",
            path: routePath.contentManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "Slideshow4Line",
          },
          {
            name: "superAdmin.contentAndExamManagement.onlineExamManagement.name",
            path: routePath.onlineExamManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "FileEditLine",
          },
          {
            name: "superAdmin.contentAndExamManagement.descriptiveExamManagement.name",
            path: routePath.descriptiveExamManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "FileChartLine",
          },
          {
            name: "superAdmin.lessonAndCourseManagement.codeManagement.name",
            path: routePath.codeManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "Coupon3Line",
          },
        ],
      },
    },
    {
      name: "superAdmin.lessonAndCourseManagement.trainingCourseAssistantManagement.name",
      iconName: "UserAddLine",
      items: {
        path: routePath.trainingCourseAssistantManagement.list(),
      },
    },
    {
      name: "teacher.myCoursesStudents.name",
      iconName: "UserAddLine",
      items: {
        path: routePath.myStudentsCourses.list(),
      },
    },
    {
      name: "superAdmin.channelManagement.name",
      iconName: "ChatQuoteLine",
      items: {
        path: routePath.channelManagement.list(),
      },
    },
    {
      name: "superAdmin.commentManagement.name",
      iconName: "QuestionAnswerLine",
      items: {
        path: routePath.commentManagement.list(),
      },
    },
    // {
    //   name: "superAdmin.reports.name",
    //   iconName: "PieChartBoxLine",
    //   items: {
    //     path: "TODO",
    //   },
    // },
    {
      name: "superAdmin.notifAndMessage.name",
      iconName: "Notification3Line",
      items: {
        path: routePath.notificationManagement.list(),
        child: [
          {
            name: "superAdmin.notifAndMessage.notifManagement.name",
            path: routePath.notificationManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "Notification3Line",
          },
          {
            name: "superAdmin.notifAndMessage.messageManagement.name",
            path: routePath.messagesManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "Message3Line",
          },
        ],
      },
    },
  ],
  [IRoleEnum.ASSISTANT]: [
    {
      name: "superAdmin.dashboard.name",
      iconName: "LayoutGridLine",
      items: {
        path: routePath.dashboard.home(),
      },
    },
    {
      name: "teacher.myTrainingCourses.name",
      iconName: "Slideshow4Line",
      items: {
        path: routePath.contentManagement.list(),
      },
    },
    {
      name: "assistant.myTeachers.name",
      iconName: "UserVoiceLine",
      items: {
        path: routePath.myTeachers.list(),
      },
    },
    {
      name: "teacher.myCoursesStudents.name",
      iconName: "UserAddLine",
      items: {
        path: routePath.myStudentsCourses.list(),
      },
    },
    {
      name: "assistant.channels.name",
      iconName: "ChatQuoteLine",
      items: {
        path: routePath.channelManagement.list(),
      },
    },
    // {
    //   name: "superAdmin.reports.name",
    //   iconName: "PieChartBoxLine",
    //   items: {
    //     path: "TODO",
    //   },
    // },
    {
      name: "superAdmin.notifAndMessage.name",
      iconName: "Notification3Line",
      items: {
        path: routePath.notificationManagement.list(),
        child: [
          {
            name: "superAdmin.notifAndMessage.notifManagement.name",
            path: routePath.notificationManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "Notification3Line",
          },
          {
            name: "superAdmin.notifAndMessage.messageManagement.name",
            path: routePath.messagesManagement.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "Message3Line",
          },
        ],
      },
    },
  ],
  [IRoleEnum.Student]: [
    {
      name: "student.home.name",
      iconName: "Home5Line",
      items: {
        path: routePath.dashboard.home(),
      },
    },
    {
      name: "student.trainingCourses.name",
      iconName: "GraduationCapLine",
      items: {
        path: routePath.student.myTrainingCourseList.list(),
        child: [
          {
            name: "student.trainingCourses.myTrainingCourses.name",
            path: routePath.student.myTrainingCourseList.list(),
            type: ISidebarChildTypeEnum.Tab,
          },
          {
            name: "student.trainingCourses.myChannels.name",
            path: routePath.student.myChannelList.list(),
            type: ISidebarChildTypeEnum.Tab,
          },
        ],
      },
    },
    {
      name: "student.myExams.name",
      iconName: "FileEditLine",
      items: {
        path: routePath.student.myExams.list(),
      },
    },
    {
      name: "student.notificationAndMessaging.name",
      iconName: "Notification3Line",
      items: {
        path: routePath.student.studentNotifications.list(),
        child: [
          {
            name: "student.notificationAndMessaging.notif.name",
            path: routePath.student.studentNotifications.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "Notification3Line",
          },
          {
            name: "student.notificationAndMessaging.message.name",
            path: routePath.student.studentMessages.list(),
            type: ISidebarChildTypeEnum.Tab,
            iconName: "Message3Line",
          },
        ],
      },
    },
    {
      name: "student.myProfile.name",
      iconName: "User6Line",
      items: {
        path: routePath.student.profile.home(),
      },
    },
  ],
  [IRoleEnum.UNKNOWN]: [],
};
